import React, { useEffect, useMemo, useState } from 'react';
import { Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography, Button } from '@mui/material';
import CastingDirectorOptions from '../../CastingDirectorOptions';
import AudioPreview, { AudioPreviewResponse } from '../../common/AudioPreview';
import { CoHostPersonality, StudioConfig } from '../../../../../types/studio.types';
import SearchIcon from '@mui/icons-material/Search';
import VoiceLibraryDialog from '../voice/VoiceLibraryDialog';

interface VoiceConfigurationSectionProps {
  host: CoHostPersonality;
  index: number;
  studioConfig: StudioConfig;
  websitePK: string;
  isLoading: boolean;
  onStudioConfigChange: (config: StudioConfig) => void;
  onPlayVoiceSample: (payload: CoHostPersonality | { host: CoHostPersonality; voiceText?: string }) => Promise<AudioPreviewResponse>;
}

const VoiceConfigurationSection: React.FC<VoiceConfigurationSectionProps> = ({
  host,
  index,
  studioConfig,
  websitePK,
  isLoading,
  onStudioConfigChange,
  onPlayVoiceSample,
}) => {
  const handleVoiceTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedCoHosts = [...studioConfig.coHosts];
    updatedCoHosts[index] = {
      ...updatedCoHosts[index],
      voiceType: event.target.value as 'castingDirector' | 'custom' | 'journalist',
    };

    onStudioConfigChange({
      ...studioConfig,
      coHosts: updatedCoHosts,
    });
  };

  const handleCoHostFieldChange = (field: keyof CoHostPersonality, value: string) => {
    const updatedCoHosts = [...studioConfig.coHosts];
    updatedCoHosts[index] = {
      ...updatedCoHosts[index],
      [field]: value,
    };

    onStudioConfigChange({
      ...studioConfig,
      coHosts: updatedCoHosts,
    });
  };

  // Helper to set both customVoiceId and customVoiceName at once
  const setCustomVoiceSelection = (voiceId: string, voiceName?: string) => {
    const updatedCoHosts = [...studioConfig.coHosts];
    updatedCoHosts[index] = {
      ...updatedCoHosts[index],
      customVoiceId: voiceId,
      customVoiceName: voiceName || updatedCoHosts[index].customVoiceName || '',
    };
    onStudioConfigChange({
      ...studioConfig,
      coHosts: updatedCoHosts,
    });
  };

  // Persisted voice sample text for Custom Voice previews
  const storageKey = useMemo(() => `voiceSampleText:${websitePK}:cohost:${index}`, [websitePK, index]);
  const [voiceSampleText, setVoiceSampleText] = useState<string>('');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (typeof saved === 'string') {
        setVoiceSampleText(saved);
      }
    } catch (e) {
      console.warn('Failed to load voice sample text', e);
    }
  }, [storageKey]);

  const persistVoiceSampleText = (value: string) => {
    setVoiceSampleText(value);
    try {
      localStorage.setItem(storageKey, value);
    } catch (e) {
      console.warn('Failed to save voice sample text', e);
    }
  };

  // Dialog state
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);

  return (
    <>
      <Grid item xs={12}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" gutterBottom>
          Voice Configuration
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset">
          <RadioGroup row value={host.voiceType} onChange={handleVoiceTypeChange}>
            <FormControlLabel
              value="castingDirector"
              control={<Radio />}
              label="Casting Director (Auto-select best matching voice)"
              disabled={isLoading}
            />
            <FormControlLabel
              value="journalist"
              control={<Radio />}
              label="Journalist's Voice"
              disabled={isLoading}
            />
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Custom Voice"
              disabled={isLoading}
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      {host.voiceType === 'castingDirector' && (
        <Grid item xs={12}>
          <CastingDirectorOptions
            host={host}
            websitePK={websitePK}
            onCastingDirectorComplete={(voiceId, explanation, blacklistedVoiceIds) => {
              const updatedCoHosts = [...studioConfig.coHosts];
              updatedCoHosts[index] = {
                ...updatedCoHosts[index],
                voiceId: voiceId,
                castingDirectorExplanation: explanation,
                blacklistedVoiceIds,
              };
              onStudioConfigChange({
                ...studioConfig,
                coHosts: updatedCoHosts,
              });
            }}
            disabled={isLoading}
          />
        </Grid>
      )}

      {host.voiceType === 'custom' && (
        <>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Custom Voice ID"
              value={host.customVoiceId || ''}
              onChange={(e) => handleCoHostFieldChange('customVoiceId', e.target.value)}
              disabled={isLoading}
              placeholder="Enter ElevenLabs Voice ID"
              helperText="Enter the Voice ID from your ElevenLabs account"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Custom Voice Name"
              value={host.customVoiceName || ''}
              onChange={(e) => handleCoHostFieldChange('customVoiceName', e.target.value)}
              disabled={isLoading}
              placeholder="Enter a name for this voice"
              helperText="A friendly name to identify this voice"
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setVoiceDialogOpen(true)}
              disabled={isLoading}
            >
              Search For Voice
            </Button>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              label="Voice sample text"
              value={voiceSampleText}
              onChange={(e) => persistVoiceSampleText(e.target.value)}
              placeholder={`Enter the exact text to generate for ${host.customVoiceName || host.nickname || host.name || 'this host'}`}
              helperText="Used for audio preview when trying voices in the library. Saved locally."
              disabled={isLoading}
            />
          </Grid>
        </>
      )}

      {host.voiceType === 'journalist' && (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 2 }}>
            The journalist's voice option will use the voice of the journalist who wrote the article, if available.
            If no journalist voice is available, it will fallback to the Casting Director to select an appropriate voice.
          </Typography>
        </Grid>
      )}

      <Grid item xs={12}>
        <AudioPreview
          buttonTitle={`Generate audio preview for ${host.nickname}`}
          clickedButtonTitle={`Generating ${host.nickname}'s voice.${host.voiceId ? '' : ' This will take a few minutes.'}..`}
          handleClickParam={{ host, voiceText: voiceSampleText || undefined }}
          handleClick={onPlayVoiceSample}
          heading={`${host.nickname}'s Voice Sample`}
          disabled={isLoading}
          helperText={host.voiceId ? 'Play a short sample of the voice.' : 'Use the casting director to generate a voice sample for this host.'}
        />
      </Grid>

      {/* Dialog for searching/selecting voices (custom mode only) */}
      {host.voiceType === 'custom' && (
        <VoiceLibraryDialog
          open={voiceDialogOpen}
          websitePK={websitePK}
          voiceSampleText={voiceSampleText}
          onClose={() => setVoiceDialogOpen(false)}
          onSelectVoice={(voice) => {
            setCustomVoiceSelection(voice.id, voice.name);
            setVoiceDialogOpen(false);
          }}
        />
      )}
    </>
  );
};

export default VoiceConfigurationSection;
